import { FC, useEffect } from 'react'
import { Box, Flex, Grid } from '@/components/atoms/Grid'
import { Body, InformationH2, LeadBase } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import styled, { useTheme } from 'styled-components'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import PageContentWrapper, {
  PageContentWrapperBackgroundStyle
} from '@/components/organisms/pageContentWrapper'
import { useDomain } from '@/providers/storeProvider'
import { Campaign, Page } from '@/network/graphql.g'
import CategoryPathHeader from '@/components/molecules/categoryPathHeader'
import { Routes } from '@/core/routes'
import Link from '@/components/atoms/link'
import InstagramIcon from '@/components/atoms/svg/InstagramIcon'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useMhub } from '@/providers/mhubProvider'
import { getMainData } from '@/utils/mainDataHelper'

type ThankYouProps = {
  campaigns: Campaign[]
}

const ThankYou: FC<ThankYouProps> = ({ campaigns }) => {
  const { t } = useTranslation('common')
  const theme = useTheme()
  const { subdomainName } = useDomain()

  const { pushThankYouPage } = useMhub()
  useEffect(() => {
    pushThankYouPage()
  }, [])

  return (
    <PageContentWrapper
      mb={96}
      backgroundStyle={PageContentWrapperBackgroundStyle.normal}
    >
      <CategoryPathHeader
        paths={[
          { url: Routes.thankYou, title: t('ThankYou.breadcrumbsTitle') }
        ]}
      />
      <Box textAlign="center">
        <InformationH2>{t('ThankYou.title')}</InformationH2>
        <Body>{t('ThankYou.subtitle')}</Body>
        <Box maxWidth={600} margin="auto">
          <ResponsiveImage
            imageUrl="/static/thankyou.jpg"
            width="700"
            height="467"
          />
          <StyledBox>
            <Body m={0} pt={3}>
              {t('ThankYou.p2')}
            </Body>
            <StyledLead m={0} pb={3}>
              <Flex justifyContent="center" alignItems="center">
                <StyledLinkHref
                  target="_blank"
                  href="https://www.instagram.com/factcool_com"
                >
                  <StyledLinkBox>
                    <InstagramIcon
                      color={theme.colors.accent}
                      width={30}
                      height={30}
                    />
                    <Box pl={10}>{t('ThankYou.link1')}</Box>
                  </StyledLinkBox>
                </StyledLinkHref>

                <StyledLinkHref
                  href={`https://instagram.com/factcool_${subdomainName}`}
                  target="_blank"
                >
                  <StyledLinkBox>{t('ThankYou.link2')}</StyledLinkBox>
                </StyledLinkHref>
              </Flex>
            </StyledLead>
          </StyledBox>
        </Box>
        <Body p={4}>{t('ThankYou.p3')}</Body>
        <Grid
          mt={16}
          gridTemplateColumns={{
            desktop: '1fr 1fr 1fr',
            tablet: '1fr 1fr',
            mobile: '1fr'
          }}
          gridRowGap={20}
          gridColumnGap={18}
        >
          {campaigns.map((item) => (
            <Link key={item.id} href={item.url}>
              <ResponsiveImage
                width={600}
                height={280}
                imageUrl={item.desktopImage}
              />
            </Link>
          ))}
        </Grid>
      </Box>
    </PageContentWrapper>
  )
}
const StyledLinkHref = styled.a`
  text-decoration: none;
`

const StyledLinkBox = styled(Box)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.accent};
  margin: 0 10px;
`

const StyledBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.listItemSeparator};
`

const StyledLead = styled(Box)`
  ${LeadBase}
`

export default ThankYou

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<Rest>> => {
    const { success, response } = await requestThrow401({
      method: sdk.getCampaigns,
      variables: {
        page: Page.ThankYou,
        withMainNavigation,
        withStore
      },
      mainResponse: (response) => response?.campaigns
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        campaigns: response?.campaigns
      }
    }
  }
)
